import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import temperService from '../../services/temper.service';

function ArchiveTemperList() {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);

  const fetchArchiveList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await temperService.getTemperArchiveList(
        page,
        pagesize,
        search || undefined,
        orderBy || undefined,
      );
      if (response.data) {
        const tempers =
          response.data.results?.data ||
          response.data.results ||
          response.data ||
          [];
        setData(Array.isArray(tempers) ? tempers : []);
        setTotalRecord(response.data.count || 0);
      }
    } catch (error) {
      message.error('Failed to fetch archived tempers');
      console.error('Error fetching archived tempers:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pagesize, search, orderBy]);

  useEffect(() => {
    fetchArchiveList();
  }, [fetchArchiveList]);

  const handleSearch = e => {
    setSearch(e.target.value);
    setPage(1);
  };

  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    if (!sortField || !sortOrder) return 'id';
    const prefix = sortOrder === 'descend' ? '-' : '';
    return `${prefix}${sortField}`;
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  };

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  const handleView = useCallback(record => {
    navigate(`/view-archive-temper/${record.id}`);
  }, [navigate]);

  const handleRestore = async id => {
    if (!id) {
      message.error('Missing ID for restore!');
      return;
    }

    try {
      await temperService.restoreTemper(id);
      message.success('Temper restored successfully');
      setSelectedRowKeys([]);
      fetchArchiveList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to restore temper';
      message.error(errorMessage);
    }
  };

  const handleBulkRestore = async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.error('Please select at least one record to restore');
      return;
    }

    try {
      await temperService.restoreTemper(selectedRowKeys);
      message.success(
        `${selectedRowKeys.length} Temper(s) restored successfully`,
      );
      setSelectedRowKeys([]);
      fetchArchiveList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to restore tempers';
      message.error(errorMessage);
    }
  };


  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      sorter: false,
      width: 80,
    },
    {
      title: 'Temper',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 150,
    },
    {
      title: 'Temper Code Old',
      dataIndex: 'temper_code_old',
      key: 'temper_code_old',
      sorter: true,
      width: 150,
      render: (text) => <span>{text || '—'}</span>,
    },
    {
      title: 'Temper Code New',
      dataIndex: 'temper_code_new',
      key: 'temper_code_new',
      sorter: true,
      width: 150,
      render: (text) => <span>{text || '—'}</span>,
    },
    {
      title: 'Section Type',
      dataIndex: 'section_type',
      key: 'section_type',
      sorter: true,
      width: 150,
      render: (text, record) => {
        const sectionType = record?.section_type;
        if (sectionType && typeof sectionType === 'object') {
          return <span>{sectionType.name || sectionType.section_type_name || 'N/A'}</span>;
        }
        return <span>{record?.section_type_name || 'N/A'}</span>;
      },
    },
    {
      title: 'Area',
      dataIndex: 'area',
      key: 'area',
      sorter: true,
      width: 120,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Dimension Unit',
      dataIndex: 'dimention_unit',
      key: 'dimention_unit',
      sorter: true,
      width: 150,
      render: (text, record) => {
        const dimUnit = record?.dimention_unit;
        if (dimUnit && typeof dimUnit === 'object') {
          return <span>{dimUnit.uom_name || dimUnit.name || 'N/A'}</span>;
        }
        return <span>{record?.dimention_unit_name || 'N/A'}</span>;
      },
    },
    {
      title: 'Elongation 50mm Min',
      dataIndex: 'elongation_50mm_min',
      key: 'elongation_50mm_min',
      sorter: true,
      width: 150,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Elongation Min',
      dataIndex: 'elongation_min',
      key: 'elongation_min',
      sorter: true,
      width: 130,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Hardness',
      dataIndex: 'hardness',
      key: 'hardness',
      sorter: true,
      width: 120,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Section Thickness Over',
      dataIndex: 'section_thickness_over',
      key: 'section_thickness_over',
      sorter: true,
      width: 180,
      render: (text) => <span>{text || '—'}</span>,
    },
    {
      title: 'Section Thickness Upto',
      dataIndex: 'section_thickness_upto',
      key: 'section_thickness_upto',
      sorter: true,
      width: 180,
      render: (text) => <span>{text || '—'}</span>,
    },
    {
      title: 'Tensile Min',
      dataIndex: 'tensile_min',
      key: 'tensile_min',
      sorter: true,
      width: 120,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Tensile Max',
      dataIndex: 'tensile_max',
      key: 'tensile_max',
      sorter: true,
      width: 120,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Yield Unit',
      dataIndex: 'yield_unit',
      key: 'yield_unit',
      sorter: true,
      width: 120,
      render: (text, record) => {
        const yieldUnit = record?.yield_unit;
        if (yieldUnit && typeof yieldUnit === 'object') {
          return <span>{yieldUnit.name || 'N/A'}</span>;
        }
        return <span>{record?.yield_unit_name || 'N/A'}</span>;
      },
    },
    {
      title: 'Yield Min',
      dataIndex: 'yield_min',
      key: 'yield_min',
      sorter: true,
      width: 120,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Yield Max',
      dataIndex: 'yield_max',
      key: 'yield_max',
      sorter: true,
      width: 120,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Electrical Conductivity Min',
      dataIndex: 'electrical_conductivity_min',
      key: 'electrical_conductivity_min',
      sorter: true,
      width: 200,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Electrical Conductivity Max',
      dataIndex: 'electrical_conductivity_max',
      key: 'electrical_conductivity_max',
      sorter: true,
      width: 200,
      render: (text) => <span>{text != null ? text : '—'}</span>,
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      width: 150,
      render: (text, record) => {
        return (
          <span>
            {record?.updated_at
              ? dayjs(record.updated_at).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      width: 150,
      render: (text, record) => {
        const updatedBy = record?.updated_by;
        if (updatedBy && typeof updatedBy === 'object') {
          return (
            <span>
              {updatedBy.first_name || ''} {updatedBy.last_name || ''}
            </span>
          );
        }
        return <span>N/A</span>;
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <span
              onClick={() => handleView(record)}
              style={{ cursor: 'pointer', marginRight: '8px' }}
            >
              <EyeOutlined style={{ fontSize: '17px' }} />
            </span>
          </Tooltip>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to restore this temper?"
              onConfirm={() => handleRestore(record.id)}
              onCancel={() => message.info('Action cancelled')}
              okText="Yes"
              cancelText="No"
            >
              <ArchiveRestore
                className="ArchiverestorImg"
                style={{ cursor: 'pointer' }}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  return (
    <>
      <div className="sticyaction">
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Archived Tempers</h2>
            <div className="bredcrumbwrp">
              <Link to="/temper-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/temper-list">Temper Master</Link> },
                  { title: 'Archived Tempers' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="large">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={search}
                style={{ width: 200 }}
                onChange={handleSearch}
              />
              <Tooltip
                title={
                  selectedRowKeys.length === 0
                    ? 'Select at least one record to restore'
                    : `Restore ${selectedRowKeys.length} selected record(s)`
                }
              >
                <Popconfirm
                  title={`Are you sure you want to restore ${selectedRowKeys.length} selected temper(s)?`}
                  onConfirm={handleBulkRestore}
                  onCancel={() => message.info('Action cancelled')}
                  okText="Yes"
                  cancelText="No"
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    type="primary"
                    disabled={selectedRowKeys.length === 0}
                  >
                    Restore Selected
                  </Button>
                </Popconfirm>
              </Tooltip>
              <Tooltip placement="top" title={'Reset Filter'}>
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="id"
            rowSelection={rowSelection}
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
            style={{ marginTop: '20px' }}
            locale={{
              emptyText: 'No archived tempers found',
            }}
          />
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>

    </>
  );
}

export default ArchiveTemperList;
